import { useState } from "react";
import { toast } from "sonner";
import { Truck, Loader2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";

export const ShippingLabelButton = ({ order }) => {
  const [label, setLabel] = useState(order.shipping_label || null);
  const [rates, setRates] = useState(null);
  const [rateId, setRateId] = useState("");
  const [loading, setLoading] = useState(false);
  const customer = order.shipping_rate || null;

  const matchCustomerRate = (list) => {
    if (!customer) return null;
    return (
      list.find((r) => customer.servicelevel_token && r.servicelevel_token === customer.servicelevel_token) ||
      list.find((r) => customer.service && r.service === customer.service && (!customer.carrier || r.provider === customer.carrier)) ||
      null
    );
  };

  const getRates = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/shipping-label/rates`);
      setRates(data.rates);
      const picked = matchCustomerRate(data.rates || []);
      if (picked) setRateId(picked.id);            // pre-select what the customer paid for
      else if (data.rates?.length) setRateId(data.rates[0].id); // else default = cheapest
      if (!data.rates?.length) toast.error("No rates returned for this shipment");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const buy = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/shipping-label`, { rate_id: rateId });
      setLabel(data.shipping_label);
      setRates(null);
      toast.success(data.reused ? "Existing label loaded" : "Shipping label created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (label) {
    return (
      <div className="flex flex-wrap items-center gap-3 text-xs font-mono" data-testid={`ship-label-result-${order.id}`}>
        <a href={label.label_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-primary hover:bg-primary/20 transition-colors" data-testid={`ship-label-pdf-${order.id}`}>
          <FileText className="h-3.5 w-3.5" /> Open label PDF
        </a>
        <span className="text-muted-foreground">
          {label.carrier ? `${label.carrier} · ` : ""}{label.tracking_number}
          {label.test ? " · TEST" : ""}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid={`ship-label-controls-${order.id}`}>
      {customer && (
        <span className="text-[11px] font-mono text-primary/80" data-testid={`ship-customer-selected-${order.id}`}>
          Customer paid for: {customer.carrier ? `${customer.carrier} · ` : ""}{customer.service || "shipping"}
          {customer.amount ? ` · ${customer.amount} ${customer.currency || "USD"}` : ""}
        </span>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {!rates ? (
          <Button size="sm" variant="outline" data-testid={`ship-label-btn-${order.id}`} disabled={loading} onClick={getRates} className="h-7 gap-1.5 text-[11px]">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Truck className="h-3 w-3" />} Shipping Label
          </Button>
        ) : (
          <>
            <Select value={rateId} onValueChange={setRateId}>
              <SelectTrigger className="h-7 w-[280px] text-[11px]" data-testid={`ship-rate-select-${order.id}`}><SelectValue placeholder="Select a rate" /></SelectTrigger>
              <SelectContent className="bg-popover">
                {rates.map((r) => (
                  <SelectItem key={r.id} value={r.id} className="text-xs">
                    {r.provider} · {r.service} · {r.amount} {r.currency}{r.estimated_days ? ` · ${r.estimated_days}d` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" data-testid={`ship-buy-btn-${order.id}`} disabled={loading || !rateId} onClick={buy} className="h-7 gap-1.5 text-[11px]">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Truck className="h-3 w-3" />} Buy label
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
